import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import LoadingBar, { type LoadingBarRef } from "react-top-loading-bar";

import { Toaster } from "@/components/ui/sonner";
import { VersionUpdateTrigger } from "@/components/version-update-trigger";

function RootError({
  code,
  title,
  description,
}: {
  code: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 p-8 text-center">
      <p className="text-muted-foreground font-mono text-6xl font-semibold">{code}</p>
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="text-muted-foreground max-w-md text-sm">{description}</p>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: () => (
    <RootError
      code="404"
      title="Page not found"
      description="The page you're looking for doesn't exist."
    />
  ),
  errorComponent: () => (
    <RootError
      code="500"
      title="Something went wrong"
      description="An internal server error occurred. Please try again later."
    />
  ),
});

function RootComponent() {
  const ref = useRef<LoadingBarRef>(null);
  const status = useRouterState({ select: (s) => s.status });
  useEffect(() => {
    if (status === "pending") ref.current?.continuousStart();
    else ref.current?.complete();
  }, [status]);

  return (
    <>
      <LoadingBar color="var(--muted-foreground)" height={2} ref={ref} shadow={false} />
      <Outlet />
      <Toaster duration={5000} />
      <VersionUpdateTrigger />
    </>
  );
}
